import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Trash2,
  Plus,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Edit,
  Play,
  Server,
  Key,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Calendar,
  Wrench,
  Globe,
  Search,
  Zap,
  ExternalLink,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  MCPServer,
  MCPServerCreate,
  MCPServerUpdate,
  MCPServerTestResult,
  MCPTool,
  AUTH_TYPES,
  AuthType,
  listMCPServers,
  createMCPServer,
  updateMCPServer,
  deleteMCPServer,
  testMCPServer,
  getEncryptionStatus,
  listServerTools,
  refreshServerTools,
  updateTool,
} from '@/services/mcpServersApi';

const MCP_TEMPLATES = [
  { name: 'salesforce', display_name: 'Salesforce', icon: '\u2601\uFE0F', url: 'https://mcp.salesforce.com/sse', auth_type: 'oauth2_client_credentials' as AuthType, description: 'CRM data, accounts, contacts, opportunities' },
  { name: 'jira', display_name: 'Jira', icon: '\uD83D\uDCCB', url: 'https://mcp.atlassian.com/jira/sse', auth_type: 'bearer' as AuthType, description: 'Issues, sprints, projects, boards' },
  { name: 'slack', display_name: 'Slack', icon: '\uD83D\uDCAC', url: 'https://mcp.slack.com/sse', auth_type: 'bearer' as AuthType, description: 'Messages, channels, users, reactions' },
  { name: 'snowflake', display_name: 'Snowflake', icon: '\u2744\uFE0F', url: '', auth_type: 'basic' as AuthType, description: 'Data warehouse queries, tables, schemas' },
  { name: 'google-workspace', display_name: 'Google Workspace', icon: '\uD83D\uDCE7', url: 'https://mcp.google.com/workspace/sse', auth_type: 'oauth2_client_credentials' as AuthType, description: 'Gmail, Drive, Calendar, Docs' },
  { name: 'github', display_name: 'GitHub', icon: '\uD83D\uDC19', url: 'https://mcp.github.com/sse', auth_type: 'bearer' as AuthType, description: 'Repositories, issues, pull requests, actions' },
  { name: 'postgres', display_name: 'PostgreSQL', icon: '\uD83D\uDC18', url: '', auth_type: 'basic' as AuthType, description: 'Database queries, tables, schemas' },
  { name: 'web-search', display_name: 'Web Search', icon: '\uD83D\uDD0D', url: 'https://mcp.search.brave.com/sse', auth_type: 'bearer' as AuthType, description: 'Web search, news, images' },
];

interface MCPServersTabProps {
  className?: string;
}

export function MCPServersTab({ className }: MCPServersTabProps) {
  const { toast } = useToast();
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [encryptionConfigured, setEncryptionConfigured] = useState(false);

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add server flow state
  const [showAddChoice, setShowAddChoice] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showSmithery, setShowSmithery] = useState(false);
  const [smitheryUrl, setSmitheryUrl] = useState('');
  const [smitherySearch, setSmitherySearch] = useState('');

  // Form state
  const [formData, setFormData] = useState<MCPServerCreate>({
    name: '',
    display_name: '',
    description: '',
    url: '',
    supports_date_override: false,
    auth_type: 'none',
    auth_config: undefined,
    restricted_teams: [],
  });
  const [showSecrets, setShowSecrets] = useState(false);

  // Auth config form fields
  const [bearerToken, setBearerToken] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [customHeaders, setCustomHeaders] = useState<Array<{ key: string; value: string }>>([]);
  const [oauthTokenUrl, setOauthTokenUrl] = useState('');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [oauthScope, setOauthScope] = useState('');

  // Testing state
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, MCPServerTestResult>>({});

  // Tool management state
  const [expandedServerId, setExpandedServerId] = useState<string | null>(null);
  const [serverTools, setServerTools] = useState<Record<string, MCPTool[]>>({});
  const [loadingToolsId, setLoadingToolsId] = useState<string | null>(null);
  const [refreshingToolsId, setRefreshingToolsId] = useState<string | null>(null);
  const [updatingToolId, setUpdatingToolId] = useState<string | null>(null);

  const fetchServers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await listMCPServers(true);
      setServers(data);
    } catch (error) {
      console.error('Failed to fetch MCP servers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load MCP servers',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const checkEncryption = useCallback(async () => {
    try {
      const status = await getEncryptionStatus();
      setEncryptionConfigured(status.configured);
    } catch (error) {
      console.error('Failed to check encryption status:', error);
    }
  }, []);

  useEffect(() => {
    fetchServers();
    checkEncryption();
  }, [fetchServers, checkEncryption]);

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      url: '',
      supports_date_override: false,
      auth_type: 'none',
      auth_config: undefined,
      restricted_teams: [],
    });
    setBearerToken('');
    setBasicUsername('');
    setBasicPassword('');
    setCustomHeaders([]);
    setOauthTokenUrl('');
    setOauthClientId('');
    setOauthClientSecret('');
    setOauthScope('');
    setShowSecrets(false);
  };

  const openAddDialog = () => {
    resetForm();
    setEditingServer(null);
    setShowAddChoice(true);
  };

  const openCustomDialog = () => {
    setShowAddChoice(false);
    resetForm();
    setEditingServer(null);
    setShowDialog(true);
  };

  const openTemplateDialog = () => {
    setShowAddChoice(false);
    setShowTemplateDialog(true);
  };

  const handleTemplateSelect = (template: typeof MCP_TEMPLATES[number]) => {
    setShowTemplateDialog(false);
    resetForm();
    setEditingServer(null);
    setFormData({
      name: template.name,
      display_name: template.display_name,
      description: template.description,
      url: template.url,
      supports_date_override: false,
      auth_type: template.auth_type,
      auth_config: undefined,
      restricted_teams: [],
    });
    setShowDialog(true);
  };

  const openSmitheryDialog = () => {
    setShowAddChoice(false);
    setSmitheryUrl('');
    setSmitherySearch('');
    setShowSmithery(true);
  };

  const handleSmitheryInstall = () => {
    if (!smitheryUrl) return;
    setShowSmithery(false);
    resetForm();
    setEditingServer(null);
    setFormData(prev => ({
      ...prev,
      url: smitheryUrl,
      name: '',
      display_name: '',
      description: '',
    }));
    setShowDialog(true);
  };

  const openEditDialog = (server: MCPServer) => {
    setEditingServer(server);
    setFormData({
      name: server.name,
      display_name: server.display_name || '',
      description: server.description || '',
      url: server.url,
      supports_date_override: server.supports_date_override || false,
      auth_type: server.auth_type as AuthType,
      auth_config: undefined,
      restricted_teams: server.restricted_teams || [],
    });
    // Clear auth fields (they need to be re-entered for security)
    setBearerToken('');
    setBasicUsername('');
    setBasicPassword('');
    setCustomHeaders([]);
    setOauthTokenUrl('');
    setOauthClientId('');
    setOauthClientSecret('');
    setOauthScope('');
    setShowSecrets(false);
    setShowDialog(true);
  };

  const buildAuthConfig = (): Record<string, unknown> | undefined => {
    switch (formData.auth_type) {
      case 'bearer':
        if (!bearerToken) return undefined;
        return { type: 'bearer', token: bearerToken };
      case 'basic':
        if (!basicUsername || !basicPassword) return undefined;
        return { type: 'basic', username: basicUsername, password: basicPassword };
      case 'custom_headers':
        const headers = customHeaders.filter(h => h.key && h.value);
        if (headers.length === 0) return undefined;
        return {
          type: 'custom_headers',
          headers: Object.fromEntries(headers.map(h => [h.key, h.value])),
        };
      case 'oauth2_client_credentials':
        if (!oauthTokenUrl || !oauthClientId || !oauthClientSecret) return undefined;
        return {
          type: 'oauth2_client_credentials',
          token_url: oauthTokenUrl,
          client_id: oauthClientId,
          client_secret: oauthClientSecret,
          scope: oauthScope || undefined,
        };
      default:
        return undefined;
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.url) {
      toast({
        title: 'Validation Error',
        description: 'Name and URL are required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const authConfig = buildAuthConfig();
      const data = {
        ...formData,
        auth_config: authConfig,
      };

      if (editingServer) {
        // Update existing
        const updateData: MCPServerUpdate = {
          display_name: data.display_name,
          description: data.description,
          url: data.url,
        };
        // Only include auth if changed
        if (authConfig || formData.auth_type === 'none') {
          updateData.auth_type = data.auth_type;
          updateData.auth_config = authConfig;
        }
        await updateMCPServer(editingServer.id, updateData);
        toast({
          title: 'Updated',
          description: `MCP server "${data.name}" updated successfully`,
        });
      } else {
        // Create new
        await createMCPServer(data as MCPServerCreate);
        toast({
          title: 'Created',
          description: `MCP server "${data.name}" created successfully`,
        });
      }

      setShowDialog(false);
      resetForm();
      fetchServers();
    } catch (error: unknown) {
      console.error('Failed to save MCP server:', error);
      const message = error instanceof Error ? error.message : 'Failed to save MCP server';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (server: MCPServer) => {
    if (!confirm(`Are you sure you want to delete "${server.display_name || server.name}"?`)) {
      return;
    }

    try {
      await deleteMCPServer(server.id);
      toast({
        title: 'Deleted',
        description: `MCP server "${server.name}" deleted`,
      });
      fetchServers();
      setTestResults(prev => {
        const next = { ...prev };
        delete next[server.id];
        return next;
      });
    } catch (error) {
      console.error('Failed to delete MCP server:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete MCP server',
        variant: 'destructive',
      });
    }
  };

  const handleTest = async (server: MCPServer) => {
    setTestingId(server.id);
    try {
      const result = await testMCPServer(server.id);
      setTestResults(prev => ({ ...prev, [server.id]: result }));
      toast({
        title: result.success ? 'Success' : 'Failed',
        description: result.message,
        variant: result.success ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Failed to test MCP server:', error);
      setTestResults(prev => ({
        ...prev,
        [server.id]: { success: false, message: 'Test request failed' },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const getTestIcon = (serverId: string) => {
    const result = testResults[serverId];
    if (!result) return null;
    if (result.success === true) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (result.success === false) return <XCircle className="h-4 w-4 text-red-500" />;
    return <AlertCircle className="h-4 w-4 text-yellow-500" />;
  };

  const getAuthTypeLabel = (authType: string) => {
    const type = AUTH_TYPES.find(t => t.value === authType);
    return type?.label || authType;
  };

  // Tool management functions
  const handleExpandServer = async (serverId: string) => {
    if (expandedServerId === serverId) {
      setExpandedServerId(null);
      return;
    }

    setExpandedServerId(serverId);

    // Load tools if not already loaded
    if (!serverTools[serverId]) {
      setLoadingToolsId(serverId);
      try {
        const tools = await listServerTools(serverId);
        setServerTools(prev => ({ ...prev, [serverId]: tools }));
      } catch (error) {
        console.error('Failed to load tools:', error);
        toast({
          title: 'Error',
          description: 'Failed to load tools',
          variant: 'destructive',
        });
      } finally {
        setLoadingToolsId(null);
      }
    }
  };

  const handleRefreshTools = async (serverId: string) => {
    setRefreshingToolsId(serverId);
    try {
      const result = await refreshServerTools(serverId);
      toast({
        title: 'Tools Refreshed',
        description: `Added: ${result.tools_added}, Updated: ${result.tools_updated}, Unavailable: ${result.tools_unavailable}`,
      });
      // Reload tools
      const tools = await listServerTools(serverId);
      setServerTools(prev => ({ ...prev, [serverId]: tools }));
    } catch (error) {
      console.error('Failed to refresh tools:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh tools',
        variant: 'destructive',
      });
    } finally {
      setRefreshingToolsId(null);
    }
  };

  const handleToggleTool = async (serverId: string, tool: MCPTool) => {
    setUpdatingToolId(tool.id);
    try {
      const updated = await updateTool(serverId, tool.id, {
        is_enabled: !tool.is_enabled,
      });
      setServerTools(prev => ({
        ...prev,
        [serverId]: prev[serverId].map(t => (t.id === tool.id ? updated : t)),
      }));
    } catch (error) {
      console.error('Failed to update tool:', error);
      toast({
        title: 'Error',
        description: 'Failed to update tool',
        variant: 'destructive',
      });
    } finally {
      setUpdatingToolId(null);
    }
  };

  const handleDateOverrideChange = async (
    serverId: string,
    tool: MCPTool,
    value: 'auto' | 'manual_yes' | 'manual_no'
  ) => {
    setUpdatingToolId(tool.id);
    try {
      let updateData: { supports_date_override?: boolean; date_override_detection_mode?: 'auto' | 'manual' };

      if (value === 'auto') {
        // Reset to auto-detection mode (will update on next refresh)
        updateData = { date_override_detection_mode: 'auto' };
      } else if (value === 'manual_yes') {
        // Manually force date override support
        updateData = { supports_date_override: true, date_override_detection_mode: 'manual' };
      } else {
        // Manually disable date override support
        updateData = { supports_date_override: false, date_override_detection_mode: 'manual' };
      }

      const updated = await updateTool(serverId, tool.id, updateData);
      setServerTools(prev => ({
        ...prev,
        [serverId]: prev[serverId].map(t => (t.id === tool.id ? updated : t)),
      }));

      toast({
        title: 'Updated',
        description: value === 'auto'
          ? 'Date override will be auto-detected on next refresh'
          : `Date override ${value === 'manual_yes' ? 'enabled' : 'disabled'} (manual)`,
      });
    } catch (error) {
      console.error('Failed to update tool date override:', error);
      toast({
        title: 'Error',
        description: 'Failed to update date override setting',
        variant: 'destructive',
      });
    } finally {
      setUpdatingToolId(null);
    }
  };

  const getToolCount = (serverId: string): { total: number; enabled: number; unavailable: number } => {
    const tools = serverTools[serverId] || [];
    return {
      total: tools.length,
      enabled: tools.filter(t => t.is_enabled && t.is_available).length,
      unavailable: tools.filter(t => !t.is_available).length,
    };
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                MCP Servers
              </CardTitle>
              <CardDescription>
                Configure external MCP (Model Context Protocol) servers for your organization.
                Agents can use these servers to access external tools.
              </CardDescription>
            </div>
            <Button onClick={openAddDialog} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Server
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!encryptionConfigured && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Encryption not configured</span>
              </div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Set API_KEY_ENCRYPTION_KEY environment variable to enable secure credential storage.
              </p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : servers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No MCP servers configured yet.</p>
              <p className="text-sm">Add a server to enable external tool access for your agents.</p>
            </div>
          ) : (
            <div className="w-full overflow-hidden">
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[4%]"></TableHead>
                  <TableHead className="w-[23%]">Name</TableHead>
                  <TableHead className="w-[30%]">URL</TableHead>
                  <TableHead className="w-[10%]">Auth</TableHead>
                  <TableHead className="w-[17%]">Status</TableHead>
                  <TableHead className="w-[16%]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {servers.map(server => {
                  const isExpanded = expandedServerId === server.id;
                  const tools = serverTools[server.id] || [];
                  const toolCount = getToolCount(server.id);

                  return (
                    <React.Fragment key={server.id}>
                      <TableRow className={isExpanded ? 'border-b-0' : ''}>
                        <TableCell className="p-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleExpandServer(server.id)}
                          >
                            {loadingToolsId === server.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="overflow-hidden">
                          <div className="truncate">
                            <div className="font-medium truncate" title={server.display_name || server.name}>
                              {server.display_name || server.name}
                            </div>
                            {server.display_name && (
                              <div className="text-sm text-muted-foreground font-mono truncate">{server.name}</div>
                            )}
                            {server.description && (
                              <div className="text-sm text-muted-foreground truncate" title={server.description}>
                                {server.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="overflow-hidden">
                          <code
                            className="text-sm bg-muted px-1 py-0.5 rounded block truncate max-w-full"
                            title={server.url}
                          >
                            {server.url}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {getAuthTypeLabel(server.auth_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={server.is_active ? 'default' : 'secondary'}>
                              {server.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {toolCount.total > 0 && (
                              <Badge variant="outline" className="text-xs">
                                <Wrench className="h-3 w-3 mr-1" />
                                {toolCount.enabled}/{toolCount.total}
                              </Badge>
                            )}
                            {getTestIcon(server.id)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleTest(server)}
                              disabled={testingId === server.id}
                              title="Test connection"
                            >
                              {testingId === server.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(server)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(server)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expandable Tools Row */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/30 p-0">
                            <div className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium flex items-center gap-2">
                                  <Wrench className="h-4 w-4" />
                                  Tools ({tools.length})
                                  {toolCount.unavailable > 0 && (
                                    <span className="text-muted-foreground">
                                      ({toolCount.unavailable} unavailable)
                                    </span>
                                  )}
                                </h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRefreshTools(server.id)}
                                  disabled={refreshingToolsId === server.id}
                                >
                                  {refreshingToolsId === server.id ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                  )}
                                  Refresh Tools
                                </Button>
                              </div>

                              {tools.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">
                                  No tools loaded. Click "Refresh Tools" to fetch from server.
                                </p>
                              ) : (
                                <div className="border rounded-md">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="w-[25%]">Tool</TableHead>
                                        <TableHead className="w-[45%]">Description</TableHead>
                                        <TableHead className="w-[15%]">Date Override</TableHead>
                                        <TableHead className="w-[15%]">Enabled</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {tools.map(tool => (
                                        <TableRow
                                          key={tool.id}
                                          className={!tool.is_available ? 'opacity-50' : ''}
                                        >
                                          <TableCell>
                                            <code className="text-sm">{tool.tool_name}</code>
                                            {!tool.is_available && (
                                              <Badge variant="destructive" className="ml-2 text-xs">
                                                Unavailable
                                              </Badge>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                                            {tool.description || '-'}
                                          </TableCell>
                                          <TableCell>
                                            <Select
                                              value={
                                                tool.date_override_detection_mode === 'auto'
                                                  ? 'auto'
                                                  : tool.supports_date_override
                                                    ? 'manual_yes'
                                                    : 'manual_no'
                                              }
                                              onValueChange={(value: 'auto' | 'manual_yes' | 'manual_no') =>
                                                handleDateOverrideChange(server.id, tool, value)
                                              }
                                              disabled={!tool.is_available || updatingToolId === tool.id}
                                            >
                                              <SelectTrigger className="h-8 w-[130px] text-xs">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="auto">
                                                  <span className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    Auto {tool.date_override_detection_mode === 'auto' && tool.supports_date_override && '✓'}
                                                  </span>
                                                </SelectItem>
                                                <SelectItem value="manual_yes">
                                                  <span className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3 text-green-600" />
                                                    Yes (Manual)
                                                  </span>
                                                </SelectItem>
                                                <SelectItem value="manual_no">
                                                  <span className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3 text-muted-foreground" />
                                                    No (Manual)
                                                  </span>
                                                </SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </TableCell>
                                          <TableCell>
                                            <Switch
                                              checked={tool.is_enabled}
                                              disabled={!tool.is_available || updatingToolId === tool.id}
                                              onCheckedChange={() => handleToggleTool(server.id, tool)}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Server Choice Dialog */}
      <Dialog open={showAddChoice} onOpenChange={setShowAddChoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add MCP Server</DialogTitle>
            <DialogDescription>
              Choose how you want to add a new MCP server.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <button
              className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left"
              onClick={openTemplateDialog}
            >
              <div className="flex-shrink-0 mt-0.5">
                <Zap className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <div className="font-medium">Quick Start Templates</div>
                <div className="text-sm text-muted-foreground">
                  Pre-configured connectors for popular services like Salesforce, Jira, Slack, and more.
                </div>
              </div>
              <ChevronRight className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
            </button>
            <button
              className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left"
              onClick={openSmitheryDialog}
            >
              <div className="flex-shrink-0 mt-0.5">
                <Globe className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="font-medium">Browse Smithery</div>
                <div className="text-sm text-muted-foreground">
                  Search and install community servers from the Smithery MCP registry.
                </div>
              </div>
              <ChevronRight className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
            </button>
            <button
              className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left"
              onClick={openCustomDialog}
            >
              <div className="flex-shrink-0 mt-0.5">
                <Server className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <div className="font-medium">Custom Server</div>
                <div className="text-sm text-muted-foreground">
                  Manually configure a server by entering the URL and authentication details.
                </div>
              </div>
              <ChevronRight className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              Quick Start Templates
            </DialogTitle>
            <DialogDescription>
              Select a template to pre-fill the server configuration. You can customize the details before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {MCP_TEMPLATES.map(template => (
              <button
                key={template.name}
                className="flex items-start gap-3 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left"
                onClick={() => handleTemplateSelect(template)}
              >
                <span className="text-2xl flex-shrink-0">{template.icon}</span>
                <div className="min-w-0">
                  <div className="font-medium">{template.display_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{template.description}</div>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {AUTH_TYPES.find(t => t.value === template.auth_type)?.label || template.auth_type}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smithery Dialog */}
      <Dialog open={showSmithery} onOpenChange={setShowSmithery}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-500" />
              Browse Smithery
            </DialogTitle>
            <DialogDescription>
              Search the Smithery MCP registry to discover and install community servers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="smithery-search">Search Servers</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="smithery-search"
                  className="pl-9"
                  placeholder="Search for MCP servers..."
                  value={smitherySearch}
                  onChange={e => setSmitherySearch(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Browse the full catalog on{' '}
                <a
                  href="https://smithery.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  smithery.ai
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>

            <div className="border-t pt-4 space-y-2">
              <Label htmlFor="smithery-url">Smithery Server URL</Label>
              <Input
                id="smithery-url"
                placeholder="https://server.smithery.ai/..."
                value={smitheryUrl}
                onChange={e => setSmitheryUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Paste a server URL from Smithery to install it.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmithery(false)}>
              Cancel
            </Button>
            <Button onClick={handleSmitheryInstall} disabled={!smitheryUrl}>
              Install
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingServer ? 'Edit MCP Server' : 'Add MCP Server'}
            </DialogTitle>
            <DialogDescription>
              Configure an external MCP server for tool access.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Server Name *</Label>
                <Input
                  id="name"
                  placeholder="my-mcp-server"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!!editingServer}
                />
                <p className="text-xs text-muted-foreground">
                  Used when referencing this server in agent configs (e.g., <code className="bg-muted px-1 rounded">mcp_servers: [my-mcp-server]</code>). Use lowercase with hyphens.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name</Label>
                <Input
                  id="display_name"
                  placeholder="My MCP Server"
                  value={formData.display_name}
                  onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Server URL *</Label>
              <Input
                id="url"
                placeholder="https://mcp.example.com/api"
                value={formData.url}
                onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Streamable HTTP endpoint URL
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this server provides..."
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                id="supports_date_override"
                checked={formData.supports_date_override || false}
                onChange={e => setFormData(prev => ({ ...prev, supports_date_override: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <div className="space-y-1">
                <Label htmlFor="supports_date_override" className="cursor-pointer">
                  Supports Date Override
                </Label>
                <p className="text-xs text-muted-foreground">
                  Server accepts X-Date-Override header for time-travel testing
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auth_type">Authentication</Label>
              <Select
                value={formData.auth_type}
                onValueChange={value => setFormData(prev => ({ ...prev, auth_type: value as AuthType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTH_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Auth-specific fields */}
            {formData.auth_type === 'bearer' && (
              <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                <Label htmlFor="bearer_token" className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Bearer Token / API Key
                </Label>
                <div className="relative">
                  <Input
                    id="bearer_token"
                    type={showSecrets ? 'text' : 'password'}
                    placeholder="sk-xxx..."
                    value={bearerToken}
                    onChange={e => setBearerToken(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowSecrets(!showSecrets)}
                  >
                    {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {formData.auth_type === 'basic' && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="basic_username">Username</Label>
                  <Input
                    id="basic_username"
                    value={basicUsername}
                    onChange={e => setBasicUsername(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="basic_password">Password</Label>
                  <div className="relative">
                    <Input
                      id="basic_password"
                      type={showSecrets ? 'text' : 'password'}
                      value={basicPassword}
                      onChange={e => setBasicPassword(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowSecrets(!showSecrets)}
                    >
                      {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {formData.auth_type === 'custom_headers' && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <Label>Custom Headers</Label>
                {customHeaders.map((header, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Header name"
                      value={header.key}
                      onChange={e => {
                        const newHeaders = [...customHeaders];
                        newHeaders[index].key = e.target.value;
                        setCustomHeaders(newHeaders);
                      }}
                    />
                    <Input
                      placeholder="Value"
                      type={showSecrets ? 'text' : 'password'}
                      value={header.value}
                      onChange={e => {
                        const newHeaders = [...customHeaders];
                        newHeaders[index].value = e.target.value;
                        setCustomHeaders(newHeaders);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setCustomHeaders(customHeaders.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCustomHeaders([...customHeaders, { key: '', value: '' }])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Header
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSecrets(!showSecrets)}
                >
                  {showSecrets ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                  {showSecrets ? 'Hide' : 'Show'} Values
                </Button>
              </div>
            )}

            {formData.auth_type === 'oauth2_client_credentials' && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="oauth_token_url">Token URL *</Label>
                  <Input
                    id="oauth_token_url"
                    placeholder="https://auth.example.com/oauth/token"
                    value={oauthTokenUrl}
                    onChange={e => setOauthTokenUrl(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="oauth_client_id">Client ID *</Label>
                    <Input
                      id="oauth_client_id"
                      value={oauthClientId}
                      onChange={e => setOauthClientId(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oauth_client_secret">Client Secret *</Label>
                    <div className="relative">
                      <Input
                        id="oauth_client_secret"
                        type={showSecrets ? 'text' : 'password'}
                        value={oauthClientSecret}
                        onChange={e => setOauthClientSecret(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowSecrets(!showSecrets)}
                      >
                        {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oauth_scope">Scope (optional)</Label>
                  <Input
                    id="oauth_scope"
                    placeholder="read write"
                    value={oauthScope}
                    onChange={e => setOauthScope(e.target.value)}
                  />
                </div>
              </div>
            )}

            {editingServer && formData.auth_type !== 'none' && (
              <p className="text-sm text-muted-foreground">
                Leave credential fields empty to keep existing credentials unchanged.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingServer ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MCPServersTab;
